import memoizeOne, { type MemoizedFn } from 'memoize-one';
import fetchJson from '@atlassian/jira-fetch/src/utils/as-json.tsx';
import { useService } from '@jira/platform__use-service/src/useService.tsx';
import type {
	PersonalizationResponse,
	SiteUserPersonalizationRequest,
	Trait,
} from './PersonalizationResponse.types.tsx';

const getPersonalizationSiteUserTraitsUrl = (cloudId: string, userId: string) =>
	`/gateway/api/tap-delivery/api/v3/personalization/site/${cloudId}/user/${userId}`;

export const fetchSiteUserPersonalization = async ({
	cloudId,
	userId,
}: SiteUserPersonalizationRequest): Promise<PersonalizationResponse['attributes']> => {
	if (!cloudId) throw new Error('empty cloudId');
	if (!userId) throw new Error('empty userId');

	const { attributes } = await fetchJson(getPersonalizationSiteUserTraitsUrl(cloudId, userId), {
		method: 'GET',
		credentials: 'include',
		headers: {
			'Content-Type': 'application/json',
			'x-atlassian-mau-ignore': 'true',
		},
	});

	return attributes;
};

export const memoizedFetchSiteUserPersonalization: MemoizedFn<
	({
		cloudId,
		userId,
	}: SiteUserPersonalizationRequest) => Promise<PersonalizationResponse['attributes']>
> = memoizeOne(
	fetchSiteUserPersonalization,
	([{ cloudId: newCloudId, userId: newUserId }], [{ cloudId: lastCloudId, userId: lastUserId }]) =>
		newCloudId === lastCloudId && newUserId === lastUserId,
);

/**
 * Use personalisation API to fetch site user traits
 */
export const useSiteUserPersonalization = (): {
	data: Trait[] | undefined;
	loading: boolean;
	error: Error | undefined;
} & {
	fetch: (requestData: SiteUserPersonalizationRequest) => Promise<Trait[] | undefined>;
} => useService(memoizedFetchSiteUserPersonalization);
